import { atom } from 'jotai';
import { atomWithObservable } from 'jotai/utils';
import { doc, onSnapshot } from 'firebase/firestore';
import { Observable } from 'rxjs';
import { db } from 'utils/firebase';
import { TActivityFeedMediaMapped, TActivityFeedMediaWithIndex, UploadStatusEnum } from 'redux-store/api/activity-feed';
import { getProjectMedia } from 'utils/services/fileCabinetService';
import type { IProjectMediaItem } from 'redux-store/api/file-cabinet';
import { selectedProjectAtom } from 'atoms/projects';
import { selectedCompanyIdAtom } from 'atoms/company';

const PAGE_SIZE = 200;

const rawProjectMediaAtom = atom<IProjectMediaItem[]>([]);
const projectMediaTotalAtom = atom<number>(0);
const projectMediaPageAtom = atom<number>(1);
const projectMediaLoadingAtom = atom<boolean>(false);

export const isProjectMediaLoadingAtom = atom((get) => get(projectMediaLoadingAtom));

/**
 * Loads project media from PostgreSQL via the unified File Cabinet API.
 * Pass `true` to reset pagination, otherwise loads the next page.
 */
export const loadProjectMediaAtom = atom(null, async (get, set, reset?: boolean) => {
  const project = get(selectedProjectAtom);
  const companyId = get(selectedCompanyIdAtom);
  if (!project?.id || !companyId) return;

  const page = reset ? 1 : get(projectMediaPageAtom);
  const hasExistingData = get(rawProjectMediaAtom).length > 0;

  if (!hasExistingData) {
    set(projectMediaLoadingAtom, true);
  }

  try {
    const response = await getProjectMedia(project.id, page, PAGE_SIZE);

    if (reset) {
      const currentItems = get(rawProjectMediaAtom);
      const isUnchanged =
        currentItems.length === response.items.length &&
        currentItems.every((cur, i) => cur.id === response.items[i].id);
      if (!isUnchanged) {
        set(rawProjectMediaAtom, response.items);
      }
      set(projectMediaPageAtom, 2);
    } else {
      set(rawProjectMediaAtom, (prev) => [...prev, ...response.items]);
      set(projectMediaPageAtom, page + 1);
    }
    set(projectMediaTotalAtom, response.total);
  } catch (err) {
    console.error('[ProjectMedia] Failed to load project media:', err);
  } finally {
    set(projectMediaLoadingAtom, false);
  }
});

export const resetProjectMediaAtom = atom(null, (_get, set) => {
  set(rawProjectMediaAtom, []);
  set(projectMediaPageAtom, 1);
  set(projectMediaTotalAtom, 0);
});

export const mediaStreamTickerAtom = atomWithObservable<number>(
  (get) => {
    const project = get(selectedProjectAtom);
    const companyId = get(selectedCompanyIdAtom);
    if (!project?.id || !companyId) {
      return new Observable<number>((subscriber) => subscriber.next(0));
    }
    const tickerRef = doc(db, `companies/${companyId}/projects/${project.id}/mediaStream/tick`);
    return new Observable<number>((subscriber) => {
      subscriber.next(0);
      let hasReceivedFirstSnapshot = false;
      let changeCount = 0;
      const unsubscribe = onSnapshot(tickerRef, () => {
        if (!hasReceivedFirstSnapshot) {
          hasReceivedFirstSnapshot = true;
          return;
        }
        changeCount += 1;
        subscriber.next(changeCount);
      });
      return () => unsubscribe();
    });
  },
  { initialValue: 0 }
);

/**
 * Derived atom that filters PostgreSQL media to images/videos only and groups by date.
 * Output shape matches TActivityFeedMediaMapped so the existing Media tab UI works unchanged.
 */
export const projectMediaAtom = atom<TActivityFeedMediaMapped>((get) => {
  const items = get(rawProjectMediaAtom);

  const visualItems = items.filter((item) => {
    const mt = item.mimeType || '';
    return mt.startsWith('image/') || mt.startsWith('video/');
  });

  const mediaItems: TActivityFeedMediaWithIndex[] = visualItems.map((item, idx) => ({
    id: `fc_${item.id}`,
    url: item.firebaseUrl || '',
    name: item.name,
    mimeType: item.mimeType,
    description: item.description || null,
    location: item.location || null,
    timestamp: item.timestamp || null,
    options: null,
    status: UploadStatusEnum.COMPLETED,
    createdAt: new Date(item.createdAt),
    profile: {
      id: item.creatorProfileId || 0,
      userId: item.creatorProfileId || 0,
      firstName: item.profileFirstName || '',
      lastName: item.profileLastName || '',
      role: '',
      avatar: { id: '', url: item.profileAvatarUrl || '', name: 'avatar' },
    },
    index: idx,
  }));

  // Sort newest first
  mediaItems.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  mediaItems.forEach((item, idx) => {
    item.index = idx;
  });

  // Group by date for the existing UI
  const groupedByDate: Record<string, TActivityFeedMediaWithIndex[]> = {};
  mediaItems.forEach((item) => {
    const dateKey = item.createdAt.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
    groupedByDate[dateKey].push(item);
  });

  const groupedItems: [string, TActivityFeedMediaWithIndex[]][] = Object.entries(groupedByDate);

  return {
    items: groupedItems,
    meta: { totalItems: mediaItems.length },
  };
});
